import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, CheckCircle2, AlertTriangle, Lock,
  ChevronDown, ChevronRight, ArrowRight,
  RotateCcw, Check,
} from 'lucide-react';
import { getJson, apiFetch } from '@/lib/http';
import { cn } from '@/lib/utils';

interface DriftedAssignmentDetail {
  storeId: number;
  storeName: string;
  mediaFormatId: number;
  mediaFormatName: string;
  plannedOperatorId: number;
  plannedOperatorName: string;
  currentOperatorId: number | null;
  currentOperatorName: string | null;
  source: 'system' | 'manual';
  diffType: 'CHANGED' | 'REMOVED' | 'ADDED';
}

interface CampaignDriftSummary {
  campaignId: number;
  campaignName: string;
  totalAssignments: number;
  driftCount: number;
  inSync: boolean;
  driftedAssignments: DriftedAssignmentDetail[];
}

interface RemovalTaskDrift {
  taskId: number;
  storeId: number;
  storeName: string;
  mediaFormatId: number | null;
  formatName: string | null;
  currentOperatorId: number;
  currentOperatorName: string;
  expectedOperatorId: number | null;
  expectedOperatorName: string | null;
}

interface RemovalDriftSummary {
  totalRemovalTasks: number;
  driftCount: number;
  inSync: boolean;
  driftedTasks: RemovalTaskDrift[];
}

interface CycleDriftCheckResult {
  cycleId: number;
  totalCampaigns: number;
  campaignsChecked: number;
  campaignsWithDrift: number;
  totalCampaignAssignments: number;
  totalCampaignDriftCount: number;
  campaigns: CampaignDriftSummary[];
  removalDrift: RemovalDriftSummary;
  allInSync: boolean;
  reconcilableCount: number;
}

interface ReconcileStepResult {
  step: string;
  campaignId?: number;
  campaignName?: string;
  status: 'success' | 'skipped';
  appliedCount: number;
  skippedManualCount: number;
  skippedMissingCount: number;
}

interface CycleReconcileResult {
  cycleId: number;
  steps: ReconcileStepResult[];
  totalApplied: number;
  totalSkipped: number;
  removalTasksUpdated: number;
}

type ModalState =
  | 'prompt'
  | 'checking'
  | 'results'
  | 'reconciling'
  | 'reconciled'
  | 'locking'
  | 'check_error'
  | 'reconcile_error';

interface PreLockCheckModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cycleId: number;
  cycleName: string;
  onProceedWithLock: (options?: { acknowledgedDrift?: boolean }) => void;
  isLocking: boolean;
}

function CampaignDriftAccordion({ campaign }: { campaign: CampaignDriftSummary }) {
  const [expanded, setExpanded] = useState(false);

  const grouped = campaign.driftedAssignments.reduce<Record<string, DriftedAssignmentDetail[]>>((acc, d) => {
    const key = `${d.storeId}|${d.storeName}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(d);
    return acc;
  }, {});

  return (
    <div className="border rounded-md">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded
            ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          }
          <span className="font-medium text-sm truncate">{campaign.campaignName}</span>
        </div>
        <Badge variant="outline" className="shrink-0 ml-2">
          {campaign.driftCount} changed
        </Badge>
      </button>

      {expanded && (
        <div className="border-t">
          {Object.entries(grouped).map(([key, assignments]) => {
            const storeName = key.split('|')[1];
            return (
              <div key={key} className="border-b last:border-b-0">
                <div className="px-3 py-2 bg-muted/30 text-xs font-medium text-muted-foreground">
                  {storeName}
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    {assignments.map((d, idx) => (
                      <tr key={idx} className="border-t first:border-t-0">
                        <td className="px-3 py-1.5 w-[30%]">{d.mediaFormatName}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{d.plannedOperatorName}</td>
                        <td className="px-1 py-1.5 w-5">
                          <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        </td>
                        <td className={cn("px-3 py-1.5", d.diffType === 'REMOVED' ? 'text-destructive' : 'font-medium')}>
                          {d.currentOperatorName || '(Removed)'}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {d.source === 'manual' && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Manual</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReconcileStepRow({ step, isActive }: { step: ReconcileStepResult; isActive: boolean }) {
  const label = step.step === 'removal_tasks'
    ? 'Removal tasks'
    : step.campaignName || `Campaign ${step.campaignId}`;

  const detail = step.status === 'success'
    ? `${step.appliedCount} updated`
    : 'Already in sync';

  return (
    <div className={cn(
      "flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-300",
      isActive && "bg-blue-50 border border-blue-200",
      step.status === 'success' && !isActive && "bg-green-50/50",
    )}>
      <div className="shrink-0">
        {isActive ? (
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
        ) : step.status === 'success' ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <Check className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium truncate block">{label}</span>
      </div>
      <span className={cn(
        "text-xs shrink-0",
        step.status === 'success' ? "text-green-700" : "text-muted-foreground"
      )}>
        {isActive ? 'Updating...' : detail}
      </span>
    </div>
  );
}

export function PreLockCheckModal({
  open,
  onOpenChange,
  cycleId,
  cycleName,
  onProceedWithLock,
  isLocking,
}: PreLockCheckModalProps) {
  const [state, setState] = useState<ModalState>('prompt');
  const [checkResult, setCheckResult] = useState<CycleDriftCheckResult | null>(null);
  const [reconcileResult, setReconcileResult] = useState<CycleReconcileResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [visibleSteps, setVisibleSteps] = useState<ReconcileStepResult[]>([]);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
  const [phaseStartedAt, setPhaseStartedAt] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const stepsEndRef = useRef<HTMLDivElement>(null);
  const previousIsLockingRef = useRef(false);

  useEffect(() => {
    if (stepsEndRef.current) {
      stepsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [visibleSteps.length]);

  useEffect(() => {
    if (!phaseStartedAt) {
      setElapsedMs(0);
      return;
    }

    setElapsedMs(Date.now() - phaseStartedAt);
    const intervalId = window.setInterval(() => {
      setElapsedMs(Date.now() - phaseStartedAt);
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [phaseStartedAt]);

  useEffect(() => {
    if (state === 'checking' || state === 'reconciling' || state === 'locking') {
      setPhaseStartedAt(Date.now());
      return;
    }

    setPhaseStartedAt(null);
  }, [state]);

  const handleClose = useCallback(() => {
    setState('prompt');
    setCheckResult(null);
    setReconcileResult(null);
    setVisibleSteps([]);
    setActiveStepIndex(-1);
    setErrorMessage(null);
    setPhaseStartedAt(null);
    setElapsedMs(0);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleDialogOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    if (state === 'locking' && previousIsLockingRef.current && !isLocking) {
      handleClose();
    }

    previousIsLockingRef.current = isLocking;
  }, [isLocking, state, handleClose]);

  const checkMutation = useMutation({
    mutationFn: async () => {
      return getJson<CycleDriftCheckResult>(`/api/cycles/${cycleId}/pre-lock-check`);
    },
    onMutate: () => {
      setState('checking');
      setErrorMessage(null);
    },
    onSuccess: (data) => {
      setCheckResult(data);
      setState('results');
    },
    onError: (error: any) => {
      setErrorMessage(error?.message || 'Something went wrong. Please try again.');
      setState('check_error');
    },
  });

  const handleReconcile = useCallback(async () => {
    setState('reconciling');
    setVisibleSteps([]);
    setActiveStepIndex(0);

    try {
      const res = await apiFetch(`/api/cycles/${cycleId}/pre-lock-reconcile`, { method: 'POST' });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Reconciliation failed (${res.status})`);
      }
      const result: CycleReconcileResult = await res.json();
      setReconcileResult(result);

      for (let i = 0; i < result.steps.length; i++) {
        setActiveStepIndex(i);
        setVisibleSteps(prev => [...prev, result.steps[i]]);
        await new Promise(r => setTimeout(r, 600));
      }

      setActiveStepIndex(-1);
      await new Promise(r => setTimeout(r, 400));
      setState('reconciled');
    } catch (error: any) {
      setErrorMessage(error?.message || 'Reconciliation failed. All changes were rolled back.');
      setState('reconcile_error');
    }
  }, [cycleId]);

  const handleLockNow = (options?: { acknowledgedDrift?: boolean }) => {
    setState('locking');
    onProceedWithLock(options);
  };

  const reconcilableCount = checkResult?.reconcilableCount ?? 0;
  const elapsedSeconds = Math.floor(elapsedMs / 1000);

  const checkingStatus =
    elapsedSeconds >= 45
      ? 'Still checking campaign and removal assignments...'
      : elapsedSeconds >= 10
        ? 'Still checking operator drift before lock...'
        : 'Checking assignments across all campaigns and removal tasks...';

  const checkingHint =
    elapsedSeconds >= 45
      ? 'This is slower than usual, but the check is still running.'
      : 'This can take up to a minute on larger cycles.';

  const lockingStatus =
    elapsedSeconds >= 15
      ? 'Still preparing the locked cycle snapshot...'
      : 'Locking cycle and preparing sign-off data...';

  const lockingHint =
    elapsedSeconds >= 15
      ? 'The lock is still running. Large cycles can take longer here.'
      : 'This usually completes quickly once pre-lock checks are done.';

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Lock Cycle: {cycleName}</DialogTitle>
          <DialogDescription>
            Locking will freeze all campaign assignments for this cycle.
          </DialogDescription>
        </DialogHeader>

        {/* --- PROMPT --- */}
        {state === 'prompt' && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Would you like to check for operator assignment changes before locking?
              This compares each campaign's planned assignments and removal tasks against the current operator configuration.
            </p>

            {/* Removal basis — lock always uses confirmed + planned */}
            <div className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Removal task basis
                </p>
                <Badge variant="default" className="text-[10px] px-1.5 py-0">
                  Confirmed + Planned
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Removals for the next cycle start from this lock snapshot, then use approved submission quantities for matching planned lines.
              </p>
              <p className="text-xs text-muted-foreground">
                Planned-only removal generation has been retired from the lock flow.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={() => checkMutation.mutate()}
                variant="outline"
                className="justify-start"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Check for assignment changes first (recommended)
              </Button>
              <Button
                onClick={() => handleLockNow({ acknowledgedDrift: true })}
                disabled={isLocking}
                className="justify-start"
                variant="outline"
              >
                {isLocking
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Lock className="h-4 w-4 mr-2" />
                }
                Skip check and lock (drift acknowledged)
              </Button>
            </div>
          </div>
        )}

        {/* --- CHECK ERROR (drift check failed — lock blocked server-side) --- */}
        {state === 'check_error' && (
          <div className="space-y-4 py-4">
            <div className="rounded-md bg-destructive/10 p-4 border border-destructive/20">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">Check failed</span>
              </div>
              <p className="text-sm text-destructive/80 mt-2">{errorMessage}</p>
              <p className="text-xs text-destructive/60 mt-1">
                The lock endpoint also runs this check server-side. Locking is blocked until the drift check succeeds.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="outline" onClick={() => setState('prompt')}>
                Retry
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* --- RECONCILE ERROR (write failed — lock blocked, no partial writes) --- */}
        {state === 'reconcile_error' && (
          <div className="space-y-4 py-4">
            <div className="rounded-md bg-destructive/10 p-4 border border-destructive/20">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">Reconciliation failed</span>
              </div>
              <p className="text-sm text-destructive/80 mt-2">{errorMessage}</p>
              <p className="text-xs text-destructive/60 mt-1">
                All changes were rolled back — no snapshots or tasks were modified. The cycle cannot be locked until reconciliation succeeds or you start over.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="outline" onClick={() => setState('prompt')}>
                Start Over
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* --- CHECKING --- */}
        {state === 'checking' && (
          <div className="space-y-4 py-8">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {checkingStatus}
              </p>
              <p className="text-xs text-muted-foreground">
                {checkingHint}
              </p>
              <p className="text-xs font-medium text-muted-foreground">
                Elapsed: {elapsedSeconds}s
              </p>
            </div>
          </div>
        )}

        {/* --- LOCKING --- */}
        {state === 'locking' && (
          <div className="space-y-4 py-8">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {lockingStatus}
              </p>
              <p className="text-xs text-muted-foreground">
                {lockingHint}
              </p>
              <p className="text-xs font-medium text-muted-foreground">
                Elapsed: {elapsedSeconds}s
              </p>
            </div>
          </div>
        )}

        {/* --- RESULTS (drift detected or all in sync) --- */}
        {state === 'results' && checkResult && (
          <div className="flex flex-col gap-4 py-4 min-h-0">
            {checkResult.allInSync ? (
              <div className="rounded-md bg-green-50 p-4 border border-green-200">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">All assignments are in sync</span>
                </div>
                <p className="text-sm text-green-700 mt-2">
                  {checkResult.totalCampaigns} campaigns ({checkResult.totalCampaignAssignments.toLocaleString()} assignments)
                  and {checkResult.removalDrift.totalRemovalTasks} removal tasks checked across all formats. No changes detected.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4 min-h-0">
                <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Assignment changes detected</span>
                  </div>
                  <div className="flex gap-4 mt-2 text-sm text-amber-700">
                    {checkResult.campaignsWithDrift > 0 && (
                      <span>{checkResult.campaignsWithDrift} campaign{checkResult.campaignsWithDrift !== 1 ? 's' : ''} with {checkResult.totalCampaignDriftCount} changed assignment{checkResult.totalCampaignDriftCount !== 1 ? 's' : ''}</span>
                    )}
                    {!checkResult.removalDrift.inSync && (
                      <span>{checkResult.removalDrift.driftCount} removal format assignment{checkResult.removalDrift.driftCount !== 1 ? 's' : ''} with stale operators</span>
                    )}
                  </div>
                </div>

                <div className="overflow-y-auto max-h-[35vh] space-y-3 pr-1">
                  {checkResult.campaigns.filter(c => !c.inSync).map(campaign => (
                    <CampaignDriftAccordion key={campaign.campaignId} campaign={campaign} />
                  ))}

                  {!checkResult.removalDrift.inSync && (() => {
                    const unresolvable = checkResult.removalDrift.driftedTasks.filter(t => t.expectedOperatorId === null);
                    const resolvable = checkResult.removalDrift.driftedTasks.filter(t => t.expectedOperatorId !== null);
                    return (
                      <div className="border rounded-md">
                        <div className="p-3 bg-orange-50/50">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">Removal Tasks</span>
                            <div className="flex items-center gap-2">
                              {unresolvable.length > 0 && (
                                <Badge variant="outline" className="text-red-700 border-red-300">
                                  {unresolvable.length} need manual fix
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-orange-700 border-orange-300">
                                {resolvable.length} stale format{resolvable.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <table className="w-full text-xs">
                          <thead className="bg-muted/30">
                            <tr>
                              <th className="text-left px-3 py-1.5 font-medium">Store</th>
                              <th className="text-left px-3 py-1.5 font-medium">Format</th>
                              <th className="text-left px-3 py-1.5 font-medium">Assigned to</th>
                              <th className="px-1 py-1.5 w-5"></th>
                              <th className="text-left px-3 py-1.5 font-medium">Should be</th>
                            </tr>
                          </thead>
                          <tbody>
                            {checkResult.removalDrift.driftedTasks.slice(0, 30).map(task => (
                              <tr key={task.taskId} className={cn("border-t", task.expectedOperatorId === null && "bg-red-50/50")}>
                                <td className="px-3 py-1.5">{task.storeName}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{task.formatName || '—'}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{task.currentOperatorName}</td>
                                <td className="px-1 py-1.5">
                                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                </td>
                                <td className={cn("px-3 py-1.5", task.expectedOperatorId !== null ? "font-medium" : "text-red-600 italic")}>
                                  {task.expectedOperatorName || 'No operator in SFA'}
                                </td>
                              </tr>
                            ))}
                            {checkResult.removalDrift.driftedTasks.length > 30 && (
                              <tr className="border-t">
                                <td colSpan={5} className="px-3 py-1.5 text-muted-foreground text-center">
                                  ...and {checkResult.removalDrift.driftedTasks.length - 30} more
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {unresolvable.length > 0 && (
                          <div className="px-3 py-2 border-t bg-red-50/30 text-xs text-red-700">
                            {unresolvable.length} format assignment{unresolvable.length !== 1 ? 's have' : ' has'} no operator in the Store Format Assignments table. These cannot be auto-updated and need to be assigned manually.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 pt-2 border-t">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              {!checkResult.allInSync && reconcilableCount > 0 && (
                <Button
                  variant="secondary"
                  onClick={handleReconcile}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Update {reconcilableCount} assignment{reconcilableCount !== 1 ? 's' : ''}, then lock
                </Button>
              )}
              <Button onClick={() => handleLockNow({ acknowledgedDrift: true })} disabled={isLocking} variant={checkResult.allInSync ? 'default' : 'outline'}>
                {isLocking
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Lock className="h-4 w-4 mr-2" />
                }
                {checkResult.allInSync ? 'Lock Cycle' : 'Lock without updating (drift acknowledged)'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* --- RECONCILING (step-by-step progress) --- */}
        {state === 'reconciling' && (
          <div className="flex flex-col gap-4 py-4 min-h-0">
            <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
              <div className="flex items-center gap-2 text-blue-800">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="font-medium">Updating operator assignments...</span>
              </div>
              <p className="text-sm text-blue-700 mt-1">
                Applying current assignments to campaign snapshots and removal tasks.
              </p>
            </div>

            <div className="overflow-y-auto max-h-[40vh] space-y-1.5 pr-1">
              {visibleSteps.map((step, idx) => (
                <ReconcileStepRow
                  key={`${step.step}-${step.campaignId || 'removal'}`}
                  step={step}
                  isActive={idx === activeStepIndex}
                />
              ))}
              <div ref={stepsEndRef} />
            </div>
          </div>
        )}

        {/* --- RECONCILED (complete, ready to lock) --- */}
        {state === 'reconciled' && reconcileResult && (
          <div className="flex flex-col gap-4 py-4 min-h-0">
            <div className="rounded-md bg-green-50 p-4 border border-green-200">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Assignments updated successfully</span>
              </div>
              <div className="mt-2 space-y-1">
                {reconcileResult.totalApplied > 0 && (
                  <p className="text-sm text-green-700">
                    {reconcileResult.totalApplied} campaign assignment{reconcileResult.totalApplied !== 1 ? 's' : ''} updated across snapshots
                  </p>
                )}
                {reconcileResult.removalTasksUpdated > 0 && (
                  <p className="text-sm text-green-700">
                    {reconcileResult.removalTasksUpdated} removal task{reconcileResult.removalTasksUpdated !== 1 ? 's' : ''} reassigned
                  </p>
                )}
                {reconcileResult.totalSkipped > 0 && (
                  <p className="text-xs text-green-600">
                    {reconcileResult.totalSkipped} assignment{reconcileResult.totalSkipped !== 1 ? 's' : ''} skipped (manual or unassigned)
                  </p>
                )}
              </div>
            </div>

            <div className="overflow-y-auto max-h-[30vh] space-y-1.5 pr-1">
              {reconcileResult.steps.map((step) => (
                <ReconcileStepRow
                  key={`${step.step}-${step.campaignId || 'removal'}`}
                  step={step}
                  isActive={false}
                />
              ))}
            </div>

            <DialogFooter className="gap-2 pt-2 border-t">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={() => handleLockNow()} disabled={isLocking}>
                {isLocking
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Lock className="h-4 w-4 mr-2" />
                }
                Lock Cycle
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
